'use client'

import { useState } from 'react'
import { motion } from 'framer-motion'
import type { PromptVersion } from '@/types/prompt.types'
import { formatRelativeDate, shortHash } from '@/lib/utils/format'

interface VersionTimelineProps {
  versions: PromptVersion[]
  currentVersionId: string
  onSelect: (version: PromptVersion) => void
  onRestore: (version: PromptVersion) => void
}

export function VersionTimeline({
  versions,
  currentVersionId,
  onSelect,
  onRestore,
}: VersionTimelineProps) {
  const [restoreConfirmId, setRestoreConfirmId] = useState<string | null>(null)

  if (versions.length === 0) {
    return (
      <div className="py-8 text-center">
        <p className="text-[var(--text-sm)] text-[var(--color-text-secondary)]">
          No versions yet. Publish to create first.
        </p>
      </div>
    )
  }

  return (
    <div className="relative" role="list" aria-label="Version history">
      {/* Vertical line */}
      <div
        className="absolute left-3 top-0 bottom-0 w-px bg-[var(--color-bg-elevated)]"
        aria-hidden="true"
      />

      <div className="space-y-1">
        {versions.map((version, index) => {
          const isCurrent = version.id === currentVersionId
          const isFirst = index === 0

          return (
            <motion.div
              key={version.id}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              role="listitem"
              className="group relative flex items-start gap-3 rounded-[var(--radius-md)] py-2.5 pl-0 pr-2 transition-colors hover:bg-[var(--color-bg-elevated)]/50"
            >
              {/* Dot */}
              <div className="relative z-10 mt-1.5 flex h-6 w-6 shrink-0 items-center justify-center">
                <div
                  className={`h-2.5 w-2.5 rounded-full ${
                    isCurrent
                      ? 'bg-[var(--color-brand-indigo)]'
                      : 'bg-[var(--color-text-muted)]'
                  }`}
                  aria-hidden="true"
                />
              </div>

              {/* Content */}
              <button
                type="button"
                onClick={() => onSelect(version)}
                className="min-w-0 flex-1 text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand-indigo)]"
                aria-label={`Version ${version.version_number}: ${version.commit_message}`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-[var(--text-sm)] font-medium text-[var(--color-text-primary)]">
                    v{version.version_number}
                  </span>
                  <code className="text-[var(--text-xs)] font-mono text-[var(--color-text-muted)]">
                    {shortHash(version.commit_hash)}
                  </code>
                  {isFirst && (
                    <span className="rounded-[var(--radius-full)] bg-[var(--color-brand-indigo)]/10 px-2 py-0.5 text-[var(--text-xs)] font-medium text-[var(--color-brand-indigo)]">
                      Current
                    </span>
                  )}
                </div>
                <p className="mt-0.5 truncate text-[var(--text-sm)] text-[var(--color-text-secondary)]">
                  &quot;{version.commit_message}&quot;
                </p>
                <time
                  dateTime={version.created_at}
                  className="mt-0.5 block text-[var(--text-xs)] text-[var(--color-text-muted)]"
                >
                  {formatRelativeDate(version.created_at)}
                </time>
              </button>

              {/* Restore button (not for current) */}
              {!isFirst && (
                <div className="shrink-0 pt-1">
                  {restoreConfirmId === version.id ? (
                    <div className="flex flex-col gap-1 rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-2 shadow-[var(--shadow-sm)]">
                      <p className="text-[var(--text-xs)] text-[var(--color-text-secondary)]">
                        Restore to This Version?
                      </p>
                      <p className="text-[var(--text-xs)] text-[var(--color-text-muted)]">
                        A new commit will be created. Your changes preserved.
                      </p>
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => {
                            onRestore(version)
                            setRestoreConfirmId(null)
                          }}
                          className="rounded-[var(--radius-sm)] bg-[var(--color-brand-indigo)] px-2 py-1 text-[var(--text-xs)] text-white hover:bg-[var(--color-brand-violet)] focus-visible:outline-2 focus-visible:outline-[var(--color-brand-indigo)]"
                        >
                          Restore
                        </button>
                        <button
                          type="button"
                          onClick={() => setRestoreConfirmId(null)}
                          className="rounded-[var(--radius-sm)] px-2 py-1 text-[var(--text-xs)] text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] focus-visible:outline-2 focus-visible:outline-[var(--color-brand-indigo)]"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setRestoreConfirmId(version.id)}
                      className="rounded-[var(--radius-sm)] px-2 py-1 text-[var(--text-xs)] text-[var(--color-text-muted)] opacity-0 transition-opacity group-hover:opacity-100 hover:text-[var(--color-brand-indigo)] focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-[var(--color-brand-indigo)]"
                    >
                      Restore
                    </button>
                  )}
                </div>
              )}
            </motion.div>
          )
        })}
      </div>
    </div>
  )
}
